import React, { useMemo } from 'react';
import { formatDate } from '../../../config/appConfig';
import { formatHm } from '../../../components/timeline-grid';
import { ReportPack, ReportPage, ReportTitleRow } from '../../../components/ui/Report';

export type ArrangementSegment = { startMinute: number; endMinute: number };

export type ArrangementProfileRow = {
  profile: string;
  notes: string | null;
  segments: ArrangementSegment[];
};

export type ArrangementCrewRow = {
  crewId: number;
  crewName: string;
  rankName?: string | null;
  watchkeeper: boolean;
  profiles: ArrangementProfileRow[];
};

export type ArrangementsReportData = {
  vesselId: number;
  vesselName: string;
  vesselAbrv?: string | null;
  imoNumber?: string | null;
  flag?: { id: number; name: string; code?: string | null } | null;
  managementCompany?: {
    id: number;
    name: string;
    code?: string | null;
    logoStorageKey?: string | null;
    logoOriginalFilename?: string | null;
  } | null;
  latestUpdate?: string | null;
  rules?: {
    name: string;
    minRest24h: number;
    minRest7d: number;
    maxWork24h: number;
    notes?: string | null;
  } | null;
  crew: ArrangementCrewRow[];
};

function formatRanges(segments: ArrangementSegment[]): string {
  if (!segments.length) return '—';
  return [...segments]
    .sort((a, b) => a.startMinute - b.startMinute)
    .map((s) => `${formatHm(s.startMinute)}–${formatHm(s.endMinute)}`)
    .join(', ');
}

function hoursOf(segments: ArrangementSegment[]): number {
  return (
    segments.reduce((sum, s) => sum + Math.max(0, s.endMinute - s.startMinute), 0) / 60
  );
}

function profileOf(row: ArrangementCrewRow, profile: string): ArrangementProfileRow | undefined {
  return row.profiles.find((p) => p.profile === profile);
}

function formatHours(n: number): string {
  if (!n) return '—';
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

/** Collapse demo / duplicate notes; show full comment text (wraps in the cell). */
function formatComments(notes: Array<string | null | undefined>): string {
  const cleaned = notes
    .map((n) =>
      (n ?? '')
        .replace(/^DEMO-SEED[^\s·]*\s*[·\-–—]?\s*/i, '')
        .replace(/\s+/g, ' ')
        .trim(),
    )
    .filter(Boolean);
  const unique = Array.from(new Set(cleaned));
  if (!unique.length) return '—';
  return unique.join('; ');
}

type ReportRow = {
  key: number;
  position: string;
  seaWatch: string;
  seaNonWatch: string;
  portWatch: string;
  portNonWatch: string;
  comments: string;
  totalSea: string;
  totalPort: string;
};

function buildRows(crew: ArrangementCrewRow[]): ReportRow[] {
  return crew.map((c) => {
    const seaWk = profileOf(c, 'WATCHKEEPER_AT_SEA');
    const seaNon = profileOf(c, 'NON_WATCHKEEPER_AT_SEA');
    const portWk = profileOf(c, 'WATCHKEEPER_IN_PORT');
    const portNon = profileOf(c, 'NON_WATCHKEEPER_IN_PORT');

    return {
      key: c.crewId,
      position: [c.rankName, c.crewName].filter(Boolean).join(' · ') || c.crewName,
      seaWatch: formatRanges(seaWk?.segments ?? []),
      seaNonWatch: formatRanges(seaNon?.segments ?? []),
      portWatch: formatRanges(portWk?.segments ?? []),
      portNonWatch: formatRanges(portNon?.segments ?? []),
      comments: formatComments([seaWk?.notes, seaNon?.notes, portWk?.notes, portNon?.notes]),
      totalSea: formatHours(hoursOf(seaWk?.segments ?? []) + hoursOf(seaNon?.segments ?? [])),
      totalPort: formatHours(hoursOf(portWk?.segments ?? []) + hoursOf(portNon?.segments ?? [])),
    };
  });
}

export type ArrangementsReportProps = {
  data: ArrangementsReportData;
  companyLogoSrc?: string | null;
};

/** Table of shipboard working arrangements — single A4 landscape sheet. */
export const ArrangementsReport: React.FC<ArrangementsReportProps> = ({
  data,
  companyLogoSrc = null,
}) => {
  const rows = useMemo(() => buildRows(data.crew), [data.crew]);
  const rules = data.rules;
  const restLine = rules
    ? `Minimum hours of rest: ${rules.minRest24h} h in any 24 h · ${rules.minRest7d} h in any 7 days · Maximum work ${rules.maxWork24h} h in any 24 h`
    : 'Minimum hours of rest / maximum hours of work as per company WRH rules and applicable MLC / STCW requirements.';

  const vesselTitle = `${data.vesselName}${data.vesselAbrv ? ` (${data.vesselAbrv})` : ''}`;

  return (
    <ReportPack className="wrh-arrangements-report-pack">
      <ReportPage
        size="landscape"
        className="ui-report-page-filled wrh-arrangements-report"
      >
        <header className="wrh-arr-header">
          <ReportTitleRow
            title="Shipboard working arrangements"
            formId="MLC / STCW"
            logoSrc={companyLogoSrc}
            logoAlt={data.managementCompany?.name || 'Management company logo'}
          />
          <table className="wrh-arr-meta">
            <tbody>
              <tr>
                <td>
                  <span className="wrh-arr-label">Name of ship</span>
                  <span className="wrh-arr-value">{vesselTitle}</span>
                </td>
                <td>
                  <span className="wrh-arr-label">Flag of ship</span>
                  <span className="wrh-arr-value">{data.flag?.name || data.flag?.code || '—'}</span>
                </td>
                <td>
                  <span className="wrh-arr-label">IMO number (if any)</span>
                  <span className="wrh-arr-value">{data.imoNumber || '—'}</span>
                </td>
                <td>
                  <span className="wrh-arr-label">Latest update of table</span>
                  <span className="wrh-arr-value">
                    {data.latestUpdate ? formatDate(data.latestUpdate) : '—'}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <p className="wrh-arr-legal">
            The maximum hours of work or minimum hours of rest are applicable in accordance with
            national law or regulation issued in conformity with the Maritime Labour Convention, 2006
            (MLC, 2006) and with any applicable collective agreement, and with the International
            Convention on Standards of Training, Certification and Watchkeeping for Seafarers, 1978, as
            amended (STCW Convention).
          </p>

          <table className="wrh-arr-limits">
            <tbody>
              <tr>
                <td>
                  <span className="wrh-arr-label">Maximum hours of work or minimum hours of rest</span>
                  <span className="wrh-arr-value">{restLine}</span>
                </td>
                <td>
                  <span className="wrh-arr-label">Other requirements</span>
                  <span className="wrh-arr-value">
                    {rules?.notes?.trim() || (rules?.name ? `Rule set: ${rules.name}` : '—')}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </header>

        <div className="wrh-arr-table-wrap">
        <table className="wrh-arr-table">
          <thead>
            <tr>
              <th rowSpan={2} className="wrh-arr-pos">
                Position / Rank
              </th>
              <th colSpan={2}>Scheduled daily work hours at sea</th>
              <th colSpan={2}>Scheduled daily work hours in port</th>
              <th rowSpan={2} className="wrh-arr-comments">
                Comments
              </th>
              <th colSpan={2} className="wrh-arr-totals-head">
                Total daily work hours
              </th>
            </tr>
            <tr>
              <th>Watchkeeping (from – to)</th>
              <th>Non-watchkeeping duties (from – to)</th>
              <th>Watchkeeping (from – to)</th>
              <th>Non-watchkeeping duties (from – to)</th>
              <th className="wrh-arr-num">At sea</th>
              <th className="wrh-arr-num">In port</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((r) => (
                <tr key={r.key}>
                  <td className="wrh-arr-pos">{r.position}</td>
                  <td>{r.seaWatch}</td>
                  <td>{r.seaNonWatch}</td>
                  <td>{r.portWatch}</td>
                  <td>{r.portNonWatch}</td>
                  <td className="wrh-arr-comments">{r.comments}</td>
                  <td className="wrh-arr-num">{r.totalSea}</td>
                  <td className="wrh-arr-num">{r.totalPort}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="wrh-arr-empty">
                  No onboard crew / arrangements for this vessel.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>

        <div className="wrh-arr-bottom">
        <footer className="wrh-arr-footer">
          <div className="wrh-arr-sign">
            <span>Signature of master</span>
            <span className="wrh-arr-sign-line" />
          </div>
          <ol className="wrh-arr-notes">
            <li>
              Terms appear in the working language(s) of the ship and in English.
            </li>
            <li>
              Hours are scheduled arrangements from vessel WRH profiles (watchkeeper /
              non-watchkeeper · at sea / in port).
            </li>
            <li>
              For watchkeeping personnel, comments may indicate unscheduled work; include such hours
              in the appropriate total daily work hours column when applicable.
            </li>
          </ol>
        </footer>
        </div>
      </ReportPage>
    </ReportPack>
  );
};

export default ArrangementsReport;
